<template>
  <div>
    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2">
        <div>
          <h5 class="mb-0 resource-title">{{ $t("Book List Record") }}</h5>
          <small class="text-muted">{{
            $t("Manage student book list records and partial receive")
          }}</small>
        </div>
      </div>

      <div class="pagination-controls-top d-flex justify-content-between align-items-center mb-2">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">{{ $t("Show") }}:</label>
            <v-select
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            {{ $t("Showing") }} <strong>{{ displayedRange.start }}</strong>
            {{ $t("to") }} <strong>{{ displayedRange.end }}</strong>
            {{ $t("of") }} <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="loadRecords"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row mb-2">
        <b-col cols="12" class="filter-toolbar book-list-record-toolbar d-flex align-items-center">
          <div class="filter-fields-group filter-fields-group--cols-5 d-flex align-items-center">
            <v-select
              v-model="filters.classID"
              :options="classes"
              :clearable="true"
              label="name"
              :reduce="(val) => val.id"
              :placeholder="$t('Class')"
              append-to-body
              class="filter-control filter-field-item filter-field-compact"
              @input="onFilterClassChange"
            />
            <v-select
              v-model="filters.sectionID"
              :options="filterSections"
              :clearable="true"
              label="section"
              :reduce="(val) => val.id"
              :placeholder="$t('Section')"
              :disabled="!filters.classID"
              append-to-body
              class="filter-control filter-field-item filter-field-compact"
              @input="onFilterSectionChange"
            />
            <v-select
              v-model="filters.status"
              :options="statusOptions"
              :clearable="true"
              label="label"
              :reduce="(val) => val.value"
              :placeholder="$t('Status')"
              append-to-body
              class="filter-control filter-field-item filter-field-compact"
              @input="onFilterChange"
            />

            <div class="filter-student-search filter-field-item">
              <div v-if="selectedStudent" class="selected-student-chip">
                <div class="selected-student-chip__avatar">
                  <feather-icon icon="UserIcon" size="14" />
                </div>
                <div class="selected-student-chip__body">
                  <span class="selected-student-chip__name">{{ selectedStudent.name }}</span>
                  <span v-if="selectedStudent.grNo" class="meta-tag meta-tag--gr">
                    {{ selectedStudent.grNo }}
                  </span>
                </div>
                <button
                  type="button"
                  class="selected-student-chip__clear"
                  :title="$t('Clear')"
                  @click="clearFilterStudent"
                >
                  <feather-icon icon="XIcon" size="13" />
                </button>
              </div>

              <div
                v-else
                class="student-search-box"
                :class="{ 'student-search-box--active': showStudentDropdown }"
              >
                <div class="student-search-input-wrap">
                  <feather-icon icon="SearchIcon" size="16" class="student-search-icon" />
                  <b-form-input
                    v-model="studentSearchQuery"
                    class="filter-search-input student-search-input"
                    :placeholder="$t('Search student...')"
                    @input="onStudentSearchChange"
                    @focus="studentSearchFocused = true"
                    @blur="onStudentSearchBlur"
                  />
                  <button
                    v-if="studentSearchQuery"
                    type="button"
                    class="student-search-clear"
                    @mousedown.prevent
                    @click="clearStudentSearchQuery"
                  >
                    <feather-icon icon="XIcon" size="14" />
                  </button>
                  <b-spinner
                    v-else-if="studentSearchLoading"
                    small
                    variant="primary"
                    class="student-search-spinner"
                  />
                </div>

                <transition name="student-search-fade">
                  <div v-if="showStudentDropdown" class="student-search-dropdown">
                    <div v-if="studentSearchQuery.length < 2" class="student-search-hint">
                      <feather-icon icon="InfoIcon" size="14" />
                      <span>{{ $t("Type at least 2 characters") }}</span>
                    </div>

                    <template v-else>
                      <div v-if="studentSearchLoading" class="student-search-state">
                        <b-spinner small variant="primary" />
                        <span>{{ $t("Searching") }}...</span>
                      </div>

                      <div
                        v-else-if="filteredStudentOptions.length === 0"
                        class="student-search-state"
                      >
                        <div class="student-search-state__icon">
                          <feather-icon icon="UserXIcon" size="22" />
                        </div>
                        <strong>{{ $t("No students found") }}</strong>
                        <small>{{ $t("Try another name or GR number") }}</small>
                      </div>

                      <template v-else>
                        <div class="student-search-dropdown__header">
                          <span>{{ filteredStudentOptions.length }} {{ $t("results") }}</span>
                        </div>
                        <div class="student-options">
                          <button
                            v-for="student in filteredStudentOptions"
                            :key="student.id"
                            type="button"
                            class="student-option"
                            @mousedown.prevent
                            @click="selectFilterStudent(student)"
                          >
                            <div class="student-option__avatar">
                              <feather-icon icon="UserIcon" size="14" />
                            </div>
                            <div class="student-option__content">
                              <span class="student-option__name">{{ student.name }}</span>
                              <span class="student-option__meta">
                                <span v-if="student.className">{{ student.className }}</span>
                                <span v-if="student.sectionName"> · {{ student.sectionName }}</span>
                              </span>
                            </div>
                            <span v-if="student.grNo" class="student-option__gr">
                              GR {{ student.grNo }}
                            </span>
                          </button>
                        </div>
                      </template>
                    </template>
                  </div>
                </transition>
              </div>
            </div>
          </div>

          <div class="filter-actions-group d-flex align-items-center ml-auto">
            <b-button
              v-if="rights.add"
              variant="gradient-success"
              v-b-tooltip.hover.top
              :title="$t('Generate Records')"
              class="action-btn-filter action-btn-filter--compact text-nowrap mr-50"
              :disabled="!canUseClassSection || generating"
              @click="confirmGenerateRecords"
            >
              <b-spinner v-if="generating" small class="mr-25" />
              <feather-icon v-else icon="UsersIcon" class="mr-25" size="15" />
              <span>{{ $t("Generate") }}</span>
            </b-button>
            <b-button
              variant="outline-secondary"
              v-b-tooltip.hover.top
              :title="$t('Summary Report')"
              class="action-btn-filter action-btn-filter--compact text-nowrap mr-50"
              @click="openSummaryReport"
            >
              <feather-icon icon="FileTextIcon" class="mr-25" size="15" />
              <span>{{ $t("Summary") }}</span>
            </b-button>
            <b-button
              variant="outline-secondary"
              v-b-tooltip.hover.top
              :title="$t('Category Report')"
              class="action-btn-filter action-btn-filter--compact text-nowrap"
              @click="openCategoryReport"
            >
              <feather-icon icon="PieChartIcon" class="mr-25" size="15" />
              <span>{{ $t("Category") }}</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <b-table
          class="planner-list-table book-list-record-table mt-1"
          :items="records"
          :fields="fields"
          :busy="dataLoading"
          show-empty
          :responsive="false"
          hover
        >
          <template #empty>
            <h3 v-if="!dataLoading" class="mt-1 mb-1 text-center">
              {{ $t("No records found") }}
            </h3>
          </template>

          <template #table-busy>
            <tbody>
              <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
                <td
                  v-for="(field, index) in fields"
                  :key="'skeleton-cell-' + index"
                  class="skeleton-cell"
                >
                  <div class="skeleton-loader"></div>
                </td>
              </tr>
            </tbody>
          </template>

          <template #cell(index)="data">
            {{ (currentPage - 1) * perPage + data.index + 1 }}
          </template>

          <template #cell(studentName)="data">
            <span
              v-b-tooltip.hover.top
              :title="data.item.studentName"
              class="student-name-cell"
            >
              {{ data.item.studentName || "—" }}
            </span>
          </template>

          <template #cell(status)="data">
            <b-badge :variant="statusVariant(data.item.status)" pill class="record-status-badge-sm">
              {{ formatStatus(data.item.status) }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <div class="d-flex justify-content-center record-actions-cell">
              <b-button
                variant="outline-primary"
                v-b-tooltip.hover.top
                :title="$t('View')"
                class="action-btn-outline action-btn-outline--sm mr-50"
                @click="openViewModal(data.item)"
              >
                <feather-icon icon="EyeIcon" size="16" />
              </b-button>
              <b-button
                v-if="rights.edit"
                variant="gradient-primary"
                v-b-tooltip.hover.top
                :title="$t('Update received qty')"
                class="action-btn-table action-btn-table--sm"
                @click="openEditModal(data.item)"
              >
                <feather-icon icon="EditIcon" size="16" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <b-modal
      v-model="showEditModal"
      size="lg"
      centered
      hide-footer
      hide-header
      modal-class="bl-edit-modal"
      content-class="bl-edit-modal-content"
      body-class="bl-edit-modal-body"
      @hidden="onEditModalHidden"
    >
      <div class="bl-edit-modal-header">
        <div class="bl-edit-modal-title-wrap">
          <div class="bl-edit-modal-icon">
            <feather-icon icon="PackageIcon" size="20" />
          </div>
          <div>
            <h5 class="bl-edit-modal-title mb-0">{{ $t("Update Received Quantity") }}</h5>
            <p class="bl-edit-modal-sub mb-0">
              {{ $t("Mark books handed out and track parent confirmation") }}
            </p>
          </div>
        </div>
        <button
          type="button"
          class="bl-edit-modal-close"
          :aria-label="$t('Close')"
          @click="showEditModal = false"
        >
          <feather-icon icon="XIcon" size="14" />
        </button>
      </div>

      <b-overlay :show="editLoading" rounded="sm" opacity="0.9" class="bl-modal-overlay">
        <template #overlay>
          <div class="bl-edit-loading bl-edit-loading--overlay">
            <b-spinner variant="primary" style="width: 2.5rem; height: 2.5rem" />
            <span>{{ $t("Loading") }}...</span>
          </div>
        </template>

        <div class="bl-edit-body">
        <div class="leave-details-section bl-edit-student-section mb-3">
          <div class="section-header">
            <feather-icon icon="UserIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">{{ $t("Student") }}</h6>
            <b-badge :variant="statusVariant(editForm.status)" pill class="ml-auto record-status-badge">
              {{ formatStatus(editForm.status) }}
            </b-badge>
          </div>
          <div class="details-meta-grid">
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Student") }}</span>
              <span class="details-value">{{ editForm.studentName || "—" }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("GR No") }}</span>
              <span class="details-value details-value-primary">{{ editForm.grNo || "—" }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Class") }}</span>
              <span class="details-value details-value-info">{{ editForm.className || "—" }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Section") }}</span>
              <span class="details-value">{{ editForm.sectionName || "—" }}</span>
            </div>
          </div>
        </div>

          <div
            v-for="(group, groupIndex) in editCategoryGroups"
            :key="'edit-cat-' + group.categoryID + '-' + groupIndex"
            class="bl-edit-category-card"
          >
          <div class="bl-edit-category-head">
            <div class="bl-edit-category-title">
              <div class="bl-view-item-icon">
                <feather-icon icon="BookIcon" size="16" />
              </div>
              <div>
                <strong>{{ group.categoryName }}</strong>
                <small class="d-block bl-edit-category-meta">
                  {{ $t("Required") }}:
                  <b-badge variant="light-secondary" pill class="record-qty-badge ml-25">
                    {{ group.totalRequiredQty }}
                  </b-badge>
                  <span class="bl-edit-category-count ml-50">
                    {{ group.lineItems.length }} {{ $t("items") }}
                  </span>
                </small>
              </div>
            </div>
          </div>

          <div class="bl-edit-line-items-wrap">
            <div class="bl-edit-line-items-hint">
              <feather-icon icon="InfoIcon" size="14" class="mr-50" />
              <span>{{ $t("Each item below is required — hand out the quantity shown") }}</span>
            </div>

            <div
              v-for="lineItem in group.lineItems"
              :key="'edit-line-' + lineItem.itemId"
              class="bl-edit-line-card"
              :class="{ 'bl-edit-line-card--done': lineItem.progressPercent >= 100 }"
            >
              <div class="bl-edit-line-card__header">
                <span class="bl-edit-line-index">{{ lineItem.lineIndex }}</span>
                <div class="bl-edit-line-info">
                  <strong class="bl-edit-line-title">
                    {{ lineItem.subCategory || lineItem.particular || lineItem.label }}
                  </strong>
                  <span
                    v-if="lineItem.subCategory && lineItem.particular"
                    class="bl-edit-line-detail"
                  >
                    {{ lineItem.particular }}
                  </span>
                </div>
                <div class="bl-edit-line-required-box">
                  <span class="bl-edit-line-required-label">{{ $t("Must hand out") }}</span>
                  <span class="bl-edit-line-required-value">{{ lineItem.requiredQty }}</span>
                </div>
              </div>

              <div class="bl-edit-line-progress">
                <div class="bl-edit-line-progress-top">
                  <span class="bl-edit-line-progress-text">
                    {{ $t("Handed out") }}:
                    <strong>{{ lineItem.receivedQty }}</strong>
                    / {{ lineItem.requiredQty }}
                  </span>
                  <b-badge :variant="statusVariant(lineItem.status)" pill class="bl-edit-line-status">
                    {{ formatStatus(lineItem.status) }}
                  </b-badge>
                </div>
                <div class="bl-edit-line-progress-track">
                  <div
                    class="bl-edit-line-progress-fill"
                    :style="{ width: lineItem.progressPercent + '%' }"
                  ></div>
                </div>
              </div>

              <div
                v-for="row in lineItem.rows"
                :key="row.rowKey"
                class="bl-edit-line-action"
                :class="{
                  'bl-edit-line-action--done': row.rowType === 'completed',
                  'bl-edit-line-action--active': row.rowType === 'active',
                }"
              >
                <div
                  v-if="row.rowType === 'completed'"
                  class="bl-edit-line-confirmed-banner"
                >
                  <feather-icon icon="CheckCircleIcon" size="15" class="mr-50" />
                  <span>
                    {{ $t("Confirmed by parent") }}:
                    <strong>{{ row.parentConfirmedQty }}</strong>
                    <template v-if="lineItem.remainingQty > 0">
                      · {{ $t("Still need to hand out") }} {{ lineItem.remainingQty }}
                    </template>
                  </span>
                </div>

                <template v-else-if="row.editable">
                  <div
                    v-if="row.parentPendingQty > 0 && !row.isNewBatch"
                    class="bl-edit-line-wait-banner"
                  >
                    <feather-icon icon="ClockIcon" size="15" class="mr-50" />
                    <span>
                      {{ $t("Waiting for parent to confirm") }}
                      <strong>{{ row.parentPendingQty }}</strong>
                      {{ $t("item(s)") }}
                    </span>
                  </div>

                  <div
                    v-if="getVisibleEditRowStats(row).length"
                    class="bl-edit-stats-row bl-edit-stats-row--compact"
                  >
                    <div
                      v-for="stat in getVisibleEditRowStats(row)"
                      :key="row.rowKey + '-' + stat.key"
                      class="bl-edit-stat bl-edit-stat--compact"
                    >
                      <span class="bl-edit-stat-label">{{ stat.label }}</span>
                      <span class="bl-edit-stat-value" :class="stat.valueClass">{{ stat.value }}</span>
                    </div>
                  </div>

                  <div class="bl-edit-input-panel bl-edit-input-panel--line">
                    <label class="bl-edit-input-label">
                      {{
                        row.isNewBatch
                          ? $t("Hand out additional quantity")
                          : isSimplePendingRow(row)
                            ? $t("Enter quantity to hand out now")
                            : $t("Update total handed out")
                      }}
                    </label>
                    <div class="bl-edit-input-row">
                      <b-form-input
                        v-if="row.isNewBatch"
                        v-model.number="row.sourceItem._batchInputQty"
                        type="number"
                        step="1"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        :min="row.minReceivedQty"
                        :max="row.maxReceivedQty"
                        class="bl-edit-qty-input"
                        :placeholder="$t('Enter qty')"
                        @keydown="blockDecimalQtyKeydown"
                        @input="clampBatchQty(row.sourceItem, row.maxReceivedQty)"
                      />
                      <b-form-input
                        v-else
                        v-model.number="row.sourceItem.receivedQty"
                        type="number"
                        step="1"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        :min="row.minReceivedQty"
                        :max="row.maxReceivedQty"
                        class="bl-edit-qty-input"
                        :placeholder="String(row.maxReceivedQty)"
                        @keydown="blockDecimalQtyKeydown"
                        @input="clampReceivedQty(row.sourceItem, row.maxReceivedQty)"
                      />
                      <span class="bl-edit-input-range">
                        {{ $t("Min") }} <strong>{{ row.minReceivedQty }}</strong>
                        ·
                        {{ $t("Max") }} <strong>{{ row.maxReceivedQty }}</strong>
                      </span>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>

          <div class="bl-category-remarks">
            <label class="bl-category-remarks-label">
              <feather-icon icon="MessageSquareIcon" size="14" class="mr-50" />
              {{ $t("Category Remarks") }}
              <span class="bl-category-remarks-name">— {{ group.categoryName }}</span>
            </label>
            <b-form-textarea
              :value="getCategoryRemarkModel(group).remarks"
              rows="2"
              max-rows="4"
              class="bl-category-remarks-input"
              :placeholder="$t('Enter remarks for this category')"
              @input="setCategoryRemark(group, $event)"
            />
          </div>
        </div>

        <div v-if="!editCategoryGroups.length" class="record-empty-state mb-2">
          <feather-icon icon="InboxIcon" size="28" class="text-muted mb-1" />
          <p class="mb-0">{{ $t("No items found") }}</p>
        </div>

        <div class="bl-edit-footer">
          <b-button variant="outline-secondary" class="bl-edit-btn-cancel" @click="showEditModal = false">
            {{ $t("Cancel") }}
          </b-button>
          <b-button
            variant="primary"
            class="bl-edit-btn-save"
            :disabled="saving || !hasEditChanges"
            @click="saveRecord"
          >
            <b-spinner v-if="saving" small class="mr-50" />
            <feather-icon v-else icon="SaveIcon" size="16" class="mr-50" />
            {{ $t("Save Changes") }}
          </b-button>
        </div>
        </div>
      </b-overlay>
    </b-modal>

    <b-modal
      v-model="showViewModal"
      size="xl"
      centered
      hide-footer
      hide-header
      modal-class="bl-edit-modal bl-view-modal"
      content-class="bl-edit-modal-content bl-view-modal-content"
      body-class="bl-edit-modal-body bl-view-modal-body"
      @hidden="onViewModalHidden"
    >
      <div class="bl-edit-modal-header">
        <div class="bl-edit-modal-title-wrap">
          <div class="bl-edit-modal-icon">
            <feather-icon icon="EyeIcon" size="20" />
          </div>
          <div>
            <h5 class="bl-edit-modal-title mb-0">{{ $t("Record Details") }}</h5>
            <p class="bl-edit-modal-sub mb-0">
              {{ $t("Book list progress and confirmation history") }}
            </p>
          </div>
        </div>
        <button
          type="button"
          class="bl-edit-modal-close"
          :aria-label="$t('Close')"
          @click="showViewModal = false"
        >
          <feather-icon icon="XIcon" size="14" />
        </button>
      </div>

      <b-overlay :show="viewLoading" rounded="sm" opacity="0.9" class="bl-modal-overlay">
        <template #overlay>
          <div class="bl-edit-loading bl-edit-loading--overlay">
            <b-spinner variant="primary" style="width: 2.5rem; height: 2.5rem" />
            <span>{{ $t("Loading") }}...</span>
          </div>
        </template>

        <div class="bl-edit-body bl-view-body">
          <div class="leave-details-section bl-view-student-section mb-3">
            <div class="section-header">
              <feather-icon icon="UserIcon" size="18" class="section-icon" />
              <h6 class="section-title mb-0">{{ $t("Student") }}</h6>
              <b-badge :variant="statusVariant(viewForm.status)" pill class="ml-auto record-status-badge">
                {{ formatStatus(viewForm.status) }}
              </b-badge>
            </div>
            <div class="details-meta-grid">
              <div class="details-meta-item">
                <span class="details-label">{{ $t("Student") }}</span>
                <span class="details-value">{{ viewForm.studentName || "—" }}</span>
              </div>
              <div class="details-meta-item">
                <span class="details-label">{{ $t("GR No") }}</span>
                <span class="details-value details-value-primary">{{ viewForm.grNo || "—" }}</span>
              </div>
              <div class="details-meta-item">
                <span class="details-label">{{ $t("Class") }}</span>
                <span class="details-value details-value-info">{{ viewForm.className || "—" }}</span>
              </div>
              <div class="details-meta-item">
                <span class="details-label">{{ $t("Section") }}</span>
                <span class="details-value">{{ viewForm.sectionName || "—" }}</span>
              </div>
            </div>
          </div>

          <div v-if="!viewForm.items.length" class="record-empty-state mb-3">
            <feather-icon icon="InboxIcon" size="28" class="text-muted mb-1" />
            <p class="mb-0">{{ $t("No items found") }}</p>
          </div>

          <div
            v-for="(group, groupIndex) in viewCategoryGroups"
            :key="'view-cat-' + group.categoryID + '-' + groupIndex"
            class="bl-edit-category-card mb-3"
          >
            <div class="bl-edit-category-head">
              <div class="bl-edit-category-title">
                <div class="bl-view-item-icon">
                  <feather-icon icon="BookIcon" size="16" />
                </div>
                <div>
                  <strong>{{ group.categoryName }}</strong>
                  <small class="d-block bl-edit-category-meta">
                    {{ $t("Required") }}:
                    <b-badge variant="light-secondary" pill class="record-qty-badge ml-25">
                      {{ group.totalRequiredQty }}
                    </b-badge>
                    <span class="bl-edit-category-count ml-50">
                      {{ group.lineItems.length }} {{ $t("items") }}
                    </span>
                  </small>
                </div>
              </div>
            </div>

            <div class="bl-edit-line-items-wrap bl-view-line-items-wrap">
              <div class="bl-edit-line-items-hint">
                <feather-icon icon="InfoIcon" size="14" class="mr-50" />
                <span>{{ $t("Required items for this student") }}</span>
              </div>

              <div
                v-for="lineItem in group.lineItems"
                :key="'view-line-' + lineItem.itemId"
                class="bl-edit-line-card bl-view-line-card"
                :class="{ 'bl-edit-line-card--done': lineItem.progressPercent >= 100 }"
              >
                <div class="bl-edit-line-card__header">
                  <span class="bl-edit-line-index">{{ lineItem.lineIndex }}</span>
                  <div class="bl-edit-line-info">
                    <strong class="bl-edit-line-title">
                      {{ lineItem.subCategory || lineItem.particular || lineItem.label }}
                    </strong>
                    <span
                      v-if="lineItem.subCategory && lineItem.particular"
                      class="bl-edit-line-detail"
                    >
                      {{ lineItem.particular }}
                    </span>
                  </div>
                  <div class="bl-edit-line-required-box">
                    <span class="bl-edit-line-required-label">{{ $t("Required") }}</span>
                    <span class="bl-edit-line-required-value">{{ lineItem.requiredQty }}</span>
                  </div>
                </div>

                <div class="bl-edit-line-progress">
                  <div class="bl-edit-line-progress-top">
                    <span class="bl-edit-line-progress-text">
                      {{ $t("Handed out") }}:
                      <strong>{{ lineItem.receivedQty }}</strong>
                      / {{ lineItem.requiredQty }}
                    </span>
                    <b-badge :variant="statusVariant(lineItem.status)" pill class="bl-edit-line-status">
                      {{ formatStatus(lineItem.status) }}
                    </b-badge>
                  </div>
                  <div class="bl-edit-line-progress-track">
                    <div
                      class="bl-edit-line-progress-fill"
                      :style="{ width: lineItem.progressPercent + '%' }"
                    ></div>
                  </div>
                </div>

                <div class="bl-edit-stats-row bl-edit-stats-row--compact bl-view-line-stats">
                  <div
                    v-for="stat in lineItem.stats"
                    :key="'stat-' + lineItem.itemId + '-' + stat.key"
                    class="bl-edit-stat bl-edit-stat--compact"
                  >
                    <span class="bl-edit-stat-label">{{ stat.label }}</span>
                    <span class="bl-edit-stat-value" :class="stat.valueClass">{{ stat.value }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="group.remarks" class="bl-category-remarks bl-category-remarks--view">
              <label class="bl-category-remarks-label">
                <feather-icon icon="MessageSquareIcon" size="14" class="mr-50" />
                {{ $t("Category Remarks") }}
              </label>
              <p class="bl-category-remarks-text mb-0">{{ group.remarks }}</p>
            </div>
          </div>

          <div class="bl-view-history-section">
            <b-tabs content-class="bl-view-tabs-content" nav-class="bl-view-tabs-nav">
              <b-tab active>
                <template #title>
                  <feather-icon icon="PackageIcon" size="15" class="mr-50" />
                  {{ $t("Books Handed Out") }}
                  <b-badge
                    v-if="viewForm.receivedHistory.length"
                    variant="light-success"
                    pill
                    class="bl-view-tab-count ml-50"
                  >
                    {{ viewForm.receivedHistory.length }}
                  </b-badge>
                </template>
                <div v-if="!viewForm.receivedHistory.length" class="bl-view-history-empty">
                  <feather-icon icon="ArchiveIcon" size="22" class="text-muted mb-50" />
                  <span>{{ $t("No receive history yet") }}</span>
                </div>
                <div v-else class="bl-history-list">
                  <div
                    v-for="entry in viewForm.receivedHistory"
                    :key="'recv-' + entry.id"
                    class="bl-history-card"
                  >
                    <div class="bl-history-card-head">
                      <span class="bl-history-card-meta">
                        <feather-icon icon="CalendarIcon" size="14" class="mr-25" />
                        {{ formatDateTime(entry.entryDate) }}
                      </span>
                      <span class="bl-history-card-meta">
                        <feather-icon icon="UserIcon" size="14" class="mr-25" />
                        {{ entry.updatedBy }}
                      </span>
                    </div>
                    <div class="bl-history-table-wrap">
                      <table class="bl-history-table">
                        <thead>
                          <tr>
                            <th>{{ $t("Category") }}</th>
                            <th class="text-center">{{ $t("Added") }}</th>
                            <th>{{ $t("Details") }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(line, idx) in entry.items"
                            :key="'recv-line-' + entry.id + '-' + idx"
                          >
                            <td class="bl-history-cat">{{ line.categoryName }}</td>
                            <td class="text-center">
                              <b-badge variant="success" pill class="record-qty-badge">
                                +{{ line.receivedQtyDelta }}
                              </b-badge>
                            </td>
                            <td class="bl-history-detail">
                              {{ $t("Total") }} {{ line.receivedQty }}
                              <template v-if="line.parentConfirmedQty != null">
                                · {{ $t("Confirmed then") }} {{ line.parentConfirmedQty }}
                              </template>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </b-tab>

              <b-tab>
                <template #title>
                  <feather-icon icon="CheckCircleIcon" size="15" class="mr-50" />
                  {{ $t("Parent Confirmations") }}
                  <b-badge
                    v-if="viewForm.parentConfirmHistory.length"
                    variant="light-info"
                    pill
                    class="bl-view-tab-count ml-50"
                  >
                    {{ viewForm.parentConfirmHistory.length }}
                  </b-badge>
                </template>
                <div v-if="!viewForm.parentConfirmHistory.length" class="bl-view-history-empty">
                  <feather-icon icon="ArchiveIcon" size="22" class="text-muted mb-50" />
                  <span>{{ $t("No parent confirmations yet") }}</span>
                </div>
                <div v-else class="bl-history-list">
                  <div
                    v-for="entry in viewForm.parentConfirmHistory"
                    :key="'confirm-' + entry.id"
                    class="bl-history-card"
                  >
                    <div class="bl-history-card-head">
                      <span class="bl-history-card-meta">
                        <feather-icon icon="CalendarIcon" size="14" class="mr-25" />
                        {{ formatDateTime(entry.entryDate) }}
                      </span>
                      <span class="bl-history-card-meta">
                        <feather-icon icon="UserCheckIcon" size="14" class="mr-25" />
                        {{ entry.confirmedBy }}
                      </span>
                      <b-badge
                        v-if="entry.confirmedQtyThisRequest > 0 && entry.items.length > 1"
                        variant="light-info"
                        pill
                        class="record-qty-badge ml-auto"
                      >
                        {{ $t("Batch") }} +{{ entry.confirmedQtyThisRequest }}
                      </b-badge>
                    </div>
                    <div class="bl-history-table-wrap">
                      <table class="bl-history-table">
                        <thead>
                          <tr>
                            <th>{{ $t("Category") }}</th>
                            <th class="text-center">{{ $t("Added") }}</th>
                            <th>{{ $t("Details") }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(line, idx) in entry.items"
                            :key="'confirm-line-' + entry.id + '-' + idx"
                          >
                            <td class="bl-history-cat">{{ line.categoryName }}</td>
                            <td class="text-center">
                              <b-badge variant="info" pill class="record-qty-badge">
                                +{{ line.confirmedQty }}
                              </b-badge>
                            </td>
                            <td class="bl-history-detail">
                              {{ $t("Total confirmed") }} {{ line.parentConfirmedQty }}
                              <span v-if="line.parentPendingQty > 0">
                                · {{ $t("Pending") }} {{ line.parentPendingQty }}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </b-tab>
            </b-tabs>
          </div>

          <div class="bl-edit-footer bl-view-footer">
            <b-button variant="outline-secondary" class="bl-edit-btn-cancel" @click="showViewModal = false">
              {{ $t("Close") }}
            </b-button>
          </div>
        </div>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BPagination,
  BModal,
  BFormInput,
  BFormTextarea,
  BSpinner,
  BBadge,
  BTab,
  BTabs,
  BOverlay,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions } from "vuex";
import Swal from "sweetalert2";
import Ripple from "vue-ripple-directive";

export default {
  name: "BookListRecord",
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BPagination,
    BModal,
    BFormInput,
    BFormTextarea,
    BSpinner,
    BBadge,
    BTab,
    BTabs,
    BOverlay,
    vSelect,
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  data() {
    return {
      rights: {},
      records: [],
      classes: [],
      classesData: [],
      filterSections: [],
      dataLoading: false,
      saving: false,
      generating: false,
      editLoading: false,
      showEditModal: false,
      showViewModal: false,
      viewLoading: false,
      currentPage: 1,
      perPage: 20,
      totalCount: 0,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      filters: {
        classID: null,
        sectionID: null,
        status: null,
      },
      selectedStudent: null,
      studentSearchQuery: "",
      studentSearchFocused: false,
      filteredStudentOptions: [],
      studentSearchLoading: false,
      studentSearchCache: {},
      studentSearchTimer: null,
      lastStudentSearchToken: 0,
      statusOptions: [
        { label: "Pending", value: "pending" },
        { label: "Awaiting Parent", value: "pending_parent_confirm" },
        { label: "Partial", value: "partial" },
        { label: "Closed", value: "closed" },
      ],
      fields: [
        {
          label: "#",
          key: "index",
          thClass: "text-center col-sn",
          tdClass: "text-center col-sn",
        },
        {
          key: "grNo",
          label: "GR",
          thClass: "text-center col-gr",
          tdClass: "text-center col-gr",
        },
        {
          key: "studentName",
          label: "Student",
          thClass: "col-student",
          tdClass: "col-student",
        },
        {
          key: "className",
          label: "Cls",
          thClass: "text-center col-cls",
          tdClass: "text-center col-cls",
        },
        {
          key: "sectionName",
          label: "Sec",
          thClass: "text-center col-sec",
          tdClass: "text-center col-sec",
        },
        {
          key: "status",
          label: "Status",
          thClass: "text-center col-sts",
          tdClass: "text-center col-sts",
        },
        {
          key: "actions",
          label: "",
          thClass: "text-center col-act",
          tdClass: "text-center col-act",
        },
      ],
      editForm: {
        recordId: 0,
        studentName: "",
        grNo: "",
        className: "",
        sectionName: "",
        status: "",
        items: [],
        categoryRemarksMap: {},
      },
      viewForm: {
        recordId: 0,
        studentName: "",
        grNo: "",
        className: "",
        sectionName: "",
        status: "",
        items: [],
        categoryRemarksMap: {},
        receivedHistory: [],
        parentConfirmHistory: [],
      },
    };
  },
  computed: {
    campusID() {
      return this.$store.state.userData.cId || this.$store.state.userData.campusID || 0;
    },
    userID() {
      return (
        this.$store.state.userData.userID ||
        this.$store.state.userData.localID ||
        this.$store.state.userData.userLinkID ||
        0
      );
    },
    canUseClassSection() {
      return Boolean(this.filters.classID && this.filters.sectionID);
    },
    displayedRange() {
      if (this.totalCount === 0) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    showStudentDropdown() {
      return this.studentSearchFocused && !this.selectedStudent;
    },
    editCategoryGroups() {
      return this.groupItemsByCategory(this.editForm.items).map((group) => {
        const remarkEntry =
          this.editForm.categoryRemarksMap[this.categoryRemarkKey(group.categoryID)] ||
          {};
        return {
          ...group,
          remarks: remarkEntry.remarks || "",
          remarkKey: this.categoryRemarkKey(group.categoryID),
          lineItems: group.items.map((item, index) => ({
            itemId: item.id,
            lineIndex: index + 1,
            subCategory: item.subCategory || "",
            particular: item.particular || "",
            label: this.getItemLineLabel(item),
            requiredQty: item.requiredQty,
            receivedQty: item.receivedQty,
            remainingQty: item.remainingQty,
            parentPendingQty: item.parentPendingQty,
            parentConfirmedQty: item.parentConfirmedQty,
            progressPercent: this.getItemProgressPercent(item),
            status: item.status,
            rows: this.buildEditDisplayRows([item]),
          })),
        };
      });
    },
    viewCategoryGroups() {
      return this.groupItemsByCategory(this.viewForm.items).map((group) => {
        const remarkEntry =
          this.viewForm.categoryRemarksMap[this.categoryRemarkKey(group.categoryID)] ||
          {};
        return {
          ...group,
          remarks: remarkEntry.remarks || "",
          lineItems: group.items.map((item, index) => ({
            itemId: item.id,
            lineIndex: index + 1,
            label: this.getItemLineLabel(item),
            subCategory: item.subCategory || "",
            particular: item.particular || "",
            requiredQty: item.requiredQty,
            receivedQty: item.receivedQty,
            remainingQty: item.remainingQty,
            parentPendingQty: item.parentPendingQty,
            parentConfirmedQty: item.parentConfirmedQty,
            progressPercent: this.getItemProgressPercent(item),
            status: item.status,
            stats: this.getViewItemStats(item),
          })),
        };
      });
    },
    hasEditChanges() {
      const qtyChanged = this.editForm.items.some((item) => {
        if (this.isItemNewBatchMode(item)) {
          return Number(item._batchInputQty) > 0;
        }
        return Number(item.receivedQty) !== Number(item._originalReceivedQty);
      });
      if (qtyChanged) return true;

      return Object.values(this.editForm.categoryRemarksMap || {}).some(
        (entry) =>
          String(entry.remarks || "").trim() !==
          String(entry._originalRemarks || "").trim()
      );
    },
  },
  created() {
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
        };
      });
    });
    this.$store.commit("setRights", right);

    const routeRights = this.$store.state.rights[this.$route.name];
    const bookListRights = this.$store.state.rights["book-list"];
    if (!routeRights && !bookListRights) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = routeRights || bookListRights;
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    this.loadClasses();
    this.loadRecords();
  },
  beforeDestroy() {
    if (this.studentSearchTimer) {
      clearTimeout(this.studentSearchTimer);
    }
  },
  methods: {
    ...mapActions({ get: "get", getString: "getString", post: "post" }),

    apiBase() {
      return this.$store.state.domain;
    },

    dbParam() {
      return encodeURIComponent(this.$store.state.userData.db);
    },

    normalizeListResponse(response) {
      if (!response) return { data: [], total: 0 };

      const payload = response;

      if (payload.data && Array.isArray(payload.data)) {
        return {
          data: payload.data,
          total: Number(
            payload.total ??
              payload.totalCount ??
              payload.recordCount ??
              payload.data.length
          ),
        };
      }

      if (Array.isArray(payload)) {
        return { data: payload, total: payload.length };
      }

      return { data: [], total: 0 };
    },

    mapRecordRow(entry) {
      const record = entry && entry.record ? entry.record : entry;
      const items = (entry && entry.items) || record.items || [];
      const recordId = this.resolveRecordId(entry, record);

      return {
        ...record,
        id: recordId,
        recordId,
        sID: record.sID ?? entry?.sID,
        grNo: record.grNo || record.gr || record.grno || "—",
        studentName: record.studentName || record.name || record.student || "—",
        className: record.meta_class || record.className || record.clsName || "—",
        sectionName: record.meta_section || record.sectionName || record.secName || "—",
        status: (record.status || "pending").toLowerCase(),
        items: Array.isArray(items) ? items : [],
      };
    },

    /** Prefer book-list record id (never confuse with item id / student id). */
    resolveRecordId(...candidates) {
      for (const source of candidates) {
        if (!source || typeof source !== "object") continue;
        const nested = source.record && typeof source.record === "object"
          ? source.record
          : null;
        const values = [
          nested?.id,
          nested?.recordID,
          nested?.recordId,
          source.recordID,
          source.recordId,
          source.id,
        ];
        for (const value of values) {
          const id = Number(value);
          if (Number.isFinite(id) && id > 0) return id;
        }
      }
      return 0;
    },

    parseRecordSelected(response, row) {
      const payload = this.unwrapLoadSelectedPayload(response);

      const record = payload.record || row;
      const items = this.extractRecordItems(payload, record, row);
      const mappedItems = (Array.isArray(items) ? items : []).map((item) =>
        this.mapItemRow(item)
      );
      const currentItemIds = mappedItems.map((item) => item.id);
      const histories = this.resolveRecordHistories(payload, mappedItems, currentItemIds);
      const categoryRemarksMap = this.buildCategoryRemarksMap(
        payload,
        mappedItems
      );

      return {
        recordId: this.resolveRecordId(record, row, payload),
        studentName: record.studentName || row.studentName || "—",
        grNo: record.grNo || record.gr || row.grNo || "—",
        className:
          record.meta_class ||
          record.className ||
          row.className ||
          "—",
        sectionName:
          record.meta_section ||
          record.sectionName ||
          row.sectionName ||
          "—",
        status: (record.status || row.status || "pending").toLowerCase(),
        items: mappedItems,
        categoryRemarksMap,
        receivedHistory: histories.receivedHistory,
        parentConfirmHistory: histories.parentConfirmHistory,
      };
    },

    unwrapLoadSelectedPayload(response) {
      let payload = response;
      if (!payload) return {};

      if (Array.isArray(payload)) {
        return { record: {}, items: payload };
      }

      if (
        payload.status === "success" &&
        payload.data != null &&
        typeof payload.data === "object"
      ) {
        payload = payload.data;
      }

      if (Array.isArray(payload)) {
        return { record: {}, items: payload };
      }

      if (
        !payload.record &&
        payload.data &&
        typeof payload.data === "object" &&
        !Array.isArray(payload.data)
      ) {
        payload = payload.data;
      }

      return payload && typeof payload === "object" ? payload : {};
    },

    extractRecordItems(payload, record, row) {
      const candidates = [
        payload.items,
        payload.recordItems,
        record.items,
        record.recordItems,
        row.items,
      ];

      for (const candidate of candidates) {
        if (Array.isArray(candidate) && candidate.length) {
          return candidate;
        }
      }

      for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
          return candidate;
        }
      }

      return [];
    },

    categoryRemarkKey(categoryID) {
      return categoryID != null ? String(categoryID) : "other";
    },

    buildCategoryRemarksMap(payload, items) {
      const map = {};
      const fromApi = Array.isArray(payload?.categoryRemarks)
        ? payload.categoryRemarks
        : [];

      fromApi.forEach((entry) => {
        const key = this.categoryRemarkKey(entry.categoryID);
        const remarks =
          entry.remarks != null
            ? String(entry.remarks)
            : entry.categoryRemarks != null
            ? String(entry.categoryRemarks)
            : "";
        map[key] = {
          categoryID: entry.categoryID,
          categoryName: entry.categoryName || "",
          remarks,
          _originalRemarks: remarks,
        };
      });

      (items || []).forEach((item) => {
        const key = this.categoryRemarkKey(item.categoryID);
        const itemRemarks =
          item.categoryRemarks != null ? String(item.categoryRemarks) : "";
        if (!map[key]) {
          map[key] = {
            categoryID: item.categoryID,
            categoryName: item.categoryName || "",
            remarks: itemRemarks,
            _originalRemarks: itemRemarks,
          };
          return;
        }
        if (!map[key].remarks && itemRemarks) {
          map[key].remarks = itemRemarks;
          map[key]._originalRemarks = itemRemarks;
        }
        if (!map[key].categoryName && item.categoryName) {
          map[key].categoryName = item.categoryName;
        }
      });

      return map;
    },

    getCategoryRemarkModel(group) {
      const key = this.categoryRemarkKey(group.categoryID);
      if (!this.editForm.categoryRemarksMap[key]) {
        this.$set(this.editForm.categoryRemarksMap, key, {
          categoryID: group.categoryID,
          categoryName: group.categoryName || "",
          remarks: "",
          _originalRemarks: "",
        });
      }
      return this.editForm.categoryRemarksMap[key];
    },

    setCategoryRemark(group, value) {
      const entry = this.getCategoryRemarkModel(group);
      this.$set(entry, "remarks", value != null ? String(value) : "");
    },

    getCategoryRemarksPayload() {
      return Object.values(this.editForm.categoryRemarksMap || {}).map(
        (entry) => ({
          categoryID: entry.categoryID,
          remarks: String(entry.remarks || "").trim(),
        })
      );
    },

    hasCategoryRemarksChanges() {
      return Object.values(this.editForm.categoryRemarksMap || {}).some(
        (entry) =>
          String(entry.remarks || "").trim() !==
          String(entry._originalRemarks || "").trim()
      );
    },

    async fetchRecordDetail(recordId, row = {}) {
      const id = this.resolveRecordId({ id: recordId }, row);
      if (!id) {
        throw new Error("Invalid book list record id");
      }

      const url =
        this.apiBase() +
        "BookListRecord/LoadSelected?db=" +
        this.dbParam() +
        "&campusID=" +
        this.campusID +
        "&id=" +
        id;
      const response = await this.getString({
        url,
        token: this.$store.state.userData.token,
      });
      return this.parseRecordSelected(response, { ...row, id, recordId: id });
    },

    resolveRecordHistories(payload, items, currentItemIds) {
      let receivedHistory = this.mapReceivedHistoryLog(payload.receivedHistory);
      let parentConfirmHistory = this.mapParentConfirmHistoryLog(
        payload.parentConfirmHistory
      );

      if (!receivedHistory.length) {
        receivedHistory = this.mergeItemLevelHistories(items, "receivedHistory");
      }
      if (!parentConfirmHistory.length) {
        parentConfirmHistory = this.mergeItemLevelHistories(
          items,
          "parentConfirmHistory"
        );
      }

      return {
        receivedHistory: this.filterHistoryToCurrentItems(
          receivedHistory,
          currentItemIds
        ),
        parentConfirmHistory: this.filterHistoryToCurrentItems(
          parentConfirmHistory,
          currentItemIds
        ),
      };
    },

    filterHistoryToCurrentItems(entries, currentItemIds) {
      if (!Array.isArray(entries) || !currentItemIds.length) return entries || [];
      const idSet = new Set(currentItemIds.map((id) => Number(id)));

      return entries
        .map((entry) => ({
          ...entry,
          items: (entry.items || []).filter(
            (line) => line.recordItemID == null || idSet.has(Number(line.recordItemID))
          ),
        }))
        .filter((entry) => entry.items && entry.items.length);
    },

    mergeItemLevelHistories(items, field) {
      const merged = [];
      const seen = new Set();
      (items || []).forEach((item) => {
        (item[field] || []).forEach((entry) => {
          const key = `${entry.id}-${(entry.items || [])
            .map((line) => line.recordItemID)
            .join(",")}`;
          if (seen.has(key)) return;
          seen.add(key);
          merged.push(entry);
        });
      });
      return merged.sort(
        (a, b) => new Date(b.entryDate).getTime() - new Date(a.entryDate).getTime()
      );
    },

    mapReceivedHistoryLog(entries) {
      if (!Array.isArray(entries)) return [];
      return entries
        .map((entry, index) => {
          if (Array.isArray(entry.items) && entry.items.length) {
            return {
              id: entry.id != null ? entry.id : `recv-${index}`,
              entryDate: entry.entryDate || entry.receivedOn || entry.date || "",
              updatedBy:
                entry.updatedBy ||
                entry.receivedBy ||
                entry.receivedByName ||
                entry.meta_receivedBy ||
                "—",
              items: entry.items.map((line) => ({
                recordItemID: line.recordItemID,
                categoryName: line.categoryName || "—",
                receivedQty: Number(line.receivedQty || 0),
                receivedQtyDelta: Number(
                  line.receivedQtyDelta != null
                    ? line.receivedQtyDelta
                    : line.qtyReceived || 0
                ),
                parentConfirmedQty: Number(line.parentConfirmedQty || 0),
              })),
            };
          }

          const delta = Number(
            entry.qtyReceived != null
              ? entry.qtyReceived
              : entry.receivedQtyDelta || entry.receivedQty || 0
          );
          return {
            id: entry.id != null ? entry.id : `recv-legacy-${index}`,
            entryDate: entry.receivedOn || entry.entryDate || entry.date || "",
            updatedBy:
              entry.receivedBy ||
              entry.updatedBy ||
              entry.receivedByName ||
              "—",
            items: [
              {
                recordItemID: entry.recordItemID,
                categoryName: entry.categoryName || "—",
                receivedQty: Number(entry.receivedQty || delta),
                receivedQtyDelta: delta,
                parentConfirmedQty: Number(entry.parentConfirmedQty || 0),
              },
            ],
          };
        })
        .sort(
          (a, b) => new Date(b.entryDate).getTime() - new Date(a.entryDate).getTime()
        );
    },

    mapParentConfirmHistoryLog(entries) {
      if (!Array.isArray(entries)) return [];
      return entries
        .map((entry, index) => {
          let items = [];
          let confirmedQtyThisRequest = Number(entry.confirmedQtyThisRequest || 0);

          if (Array.isArray(entry.items) && entry.items.length) {
            items = entry.items.map((line) => ({
              recordItemID: line.recordItemID,
              categoryName: line.categoryName || "—",
              confirmedQty: Number(line.confirmedQty || 0),
              parentConfirmedQty: Number(line.parentConfirmedQty || 0),
              parentPendingQty: Number(line.parentPendingQty || 0),
            }));
            if (!confirmedQtyThisRequest) {
              confirmedQtyThisRequest = items.reduce(
                (sum, line) => sum + line.confirmedQty,
                0
              );
            }
          } else if (
            entry.recordItemID != null ||
            entry.categoryName ||
            entry.confirmedQty != null
          ) {
            items = [
              {
                recordItemID: entry.recordItemID,
                categoryName: entry.categoryName || "—",
                confirmedQty: Number(entry.confirmedQty || 0),
                parentConfirmedQty: Number(entry.parentConfirmedQty || 0),
                parentPendingQty: Number(entry.parentPendingQty || 0),
              },
            ];
            if (!confirmedQtyThisRequest) {
              confirmedQtyThisRequest = Number(entry.confirmedQty || 0);
            }
          }

          return {
            id: entry.id != null ? entry.id : `confirm-${index}`,
            entryDate: entry.entryDate || entry.confirmedOn || entry.date || "",
            confirmedBy: entry.confirmedBy || entry.confirmedByName || "—",
            confirmedQtyThisRequest,
            items,
          };
        })
        .filter((entry) => entry.items.length)
        .sort(
          (a, b) => new Date(b.entryDate).getTime() - new Date(a.entryDate).getTime()
        );
    },

    getItemProgressPercent(item) {
      const required = Number(item.requiredQty) || 0;
      const received = Number(item.receivedQty) || 0;
      if (!required) return 0;
      return Math.min(Math.round((received / required) * 100), 100);
    },

    isSimplePendingRow(row) {
      return (
        row.rowType === "active" &&
        !row.isNewBatch &&
        Number(row.receivedQty) === 0 &&
        Number(row.parentConfirmedQty) === 0 &&
        Number(row.parentPendingQty) === 0
      );
    },

    getVisibleEditRowStats(row) {
      if (this.isSimplePendingRow(row)) return [];
      return this.getEditRowStats(row).filter((stat) => {
        if (stat.key === "remaining") return Number(stat.value) > 0;
        if (stat.key === "pending") return Number(stat.value) > 0;
        if (stat.key === "parentConfirmed") return Number(stat.value) > 0;
        if (stat.key === "received") return Number(stat.value) > 0;
        return true;
      });
    },

    getItemLineLabel(item) {
      const sub = (item.subCategory || "").trim();
      const particular = (item.particular || "").trim();
      if (sub && particular && sub !== particular) {
        return `${sub} — ${particular}`;
      }
      return sub || particular || item.categoryName || "—";
    },

    groupItemsByCategory(items) {
      const groups = new Map();

      (items || []).forEach((item) => {
        const key =
          item.categoryID != null
            ? String(item.categoryID)
            : (item.categoryName || "other").trim().toLowerCase();

        if (!groups.has(key)) {
          groups.set(key, {
            categoryID: item.categoryID,
            categoryName: item.categoryName || "—",
            totalRequiredQty: 0,
            items: [],
          });
        }

        const group = groups.get(key);
        group.items.push(item);
        group.totalRequiredQty += Number(item.requiredQty) || 0;
      });

      return Array.from(groups.values());
    },

    getViewItemStats(item) {
      return [
        {
          key: "received",
          label: this.$t("Received"),
          value: item.receivedQty,
          valueClass: "bl-stat--success",
        },
        {
          key: "parentConfirmed",
          label: this.$t("Parent Confirmed"),
          value: item.parentConfirmedQty,
          valueClass: "bl-stat--info",
        },
        {
          key: "remaining",
          label: this.$t("Remaining"),
          value: item.remainingQty,
          valueClass: "bl-stat--warning",
        },
        {
          key: "pending",
          label: this.$t("Pending Confirm"),
          value: item.parentPendingQty,
          valueClass: "bl-stat--primary",
        },
      ];
    },

    mapItemRow(item) {
      const requiredQty = this.toIntegerQty(
        item.requiredQty != null ? item.requiredQty : item.qty,
        0
      );
      const receivedQty = this.toIntegerQty(item.receivedQty, 0);
      const parentConfirmedQty = this.toIntegerQty(item.parentConfirmedQty, 0);
      const remainingQty =
        item.remainingQty != null
          ? this.toIntegerQty(item.remainingQty, 0)
          : Math.max(requiredQty - receivedQty, 0);
      const parentPendingQty =
        item.parentPendingQty != null
          ? this.toIntegerQty(item.parentPendingQty, 0)
          : Math.max(receivedQty - parentConfirmedQty, 0);
      const isParentConfirmComplete =
        item.isParentConfirmComplete != null
          ? Boolean(item.isParentConfirmComplete)
          : parentPendingQty === 0 && receivedQty > 0;
      return {
        ...item,
        id: item.id != null ? item.id : item.recordItemID,
        categoryID: item.categoryID ?? item.categoryId ?? item.catID ?? null,
        categoryName: item.categoryName || item.name || item.category || "—",
        subCategory: item.subCategory || item.sub_category || "",
        particular: item.particular || item.itemName || item.item || "",
        categoryRemarks:
          item.categoryRemarks != null ? String(item.categoryRemarks) : "",
        requiredQty,
        receivedQty,
        _originalReceivedQty: receivedQty,
        parentConfirmedQty,
        remainingQty,
        parentPendingQty,
        isParentConfirmComplete,
        _batchInputQty: 0,
        status: (item.status || "pending").toLowerCase(),
        receivedHistory: this.mapReceivedHistoryLog(
          item.receivedHistory || item.history
        ),
        parentConfirmHistory: this.mapParentConfirmHistoryLog(
          item.parentConfirmHistory
        ),
      };
    },

    isItemNewBatchMode(item) {
      return (
        item.parentConfirmedQty > 0 &&
        item.isParentConfirmComplete === true &&
        item.remainingQty > 0
      );
    },

    activeRowStatus(item, isNewBatch) {
      if (isNewBatch) return "pending";
      if (item.parentPendingQty > 0) return "pending_parent_confirm";
      return item.status || "pending";
    },

    getEditRowStats(row) {
      const stats = [];
      if (!row.editable || row.isNewBatch) {
        stats.push({
          key: "received",
          label: this.$t("Received"),
          value: row.receivedQty,
          valueClass: "bl-stat--success",
        });
      }
      stats.push(
        {
          key: "parentConfirmed",
          label: this.$t("Parent Confirmed"),
          value: row.parentConfirmedQty,
          valueClass: "bl-stat--info",
        },
        {
          key: "remaining",
          label: this.$t("Remaining"),
          value: row.remainingQty,
          valueClass: "bl-stat--warning",
        },
        {
          key: "pending",
          label: this.$t("Pending Confirm"),
          value: row.parentPendingQty,
          valueClass: "bl-stat--primary",
        }
      );
      return stats;
    },

    buildEditDisplayRows(items) {
      if (!Array.isArray(items)) return [];
      const rows = [];

      items.forEach((item) => {
        const status = (item.status || "pending").toLowerCase();
        const showCompletedRow =
          item.parentConfirmedQty > 0 && item.isParentConfirmComplete === true;
        const showActiveRow =
          item.remainingQty > 0 ||
          item.parentPendingQty > 0 ||
          (item.receivedQty === 0 && status === "pending");
        const isNewBatch = showCompletedRow && item.remainingQty > 0;

        if (showCompletedRow) {
          rows.push({
            rowKey: `${item.id}-completed`,
            itemId: item.id,
            rowType: "completed",
            isSubRow: false,
            isNewBatch: false,
            categoryName: item.categoryName,
            requiredQty: item.requiredQty,
            receivedQty: item.parentConfirmedQty,
            parentConfirmedQty: item.parentConfirmedQty,
            remainingQty: 0,
            parentPendingQty: 0,
            status: item.remainingQty > 0 ? "awaiting_more_books" : "closed",
            editable: false,
          });
        }

        if (showActiveRow) {
          item._batchInputQty = Number(item._batchInputQty) || 0;
          rows.push({
            rowKey: `${item.id}-active`,
            itemId: item.id,
            rowType: "active",
            isSubRow: showCompletedRow,
            isNewBatch,
            categoryName: item.categoryName,
            requiredQty: item.requiredQty,
            receivedQty: isNewBatch ? 0 : item.receivedQty,
            minReceivedQty: isNewBatch ? 1 : item._originalReceivedQty,
            maxReceivedQty: isNewBatch ? item.remainingQty : item.requiredQty,
            parentConfirmedQty: item.parentConfirmedQty,
            remainingQty: item.remainingQty,
            parentPendingQty: item.parentPendingQty,
            status: this.activeRowStatus(item, isNewBatch),
            editable: true,
            sourceItem: item,
          });
        }
      });

      return rows;
    },

    toIntegerQty(value, fallback = 0) {
      if (value === "" || value == null) return fallback;
      const num = Number(value);
      if (Number.isNaN(num)) return fallback;
      return Math.trunc(num);
    },

    blockDecimalQtyKeydown(event) {
      if ([".", ",", "e", "E", "-", "+"].includes(event.key)) {
        event.preventDefault();
      }
    },

    clampReceivedQty(item, maxQty) {
      const min = this.toIntegerQty(item._originalReceivedQty, 0);
      const max = this.toIntegerQty(
        maxQty != null ? maxQty : item.requiredQty,
        0
      );

      if (item.receivedQty === "" || item.receivedQty == null) return;

      let qty = this.toIntegerQty(item.receivedQty, min);
      if (qty < min) qty = min;
      else if (qty > max) qty = max;
      item.receivedQty = qty;
    },

    clampBatchQty(item, maxQty) {
      const max = this.toIntegerQty(maxQty, 0);
      if (item._batchInputQty === "" || item._batchInputQty == null) return;

      let qty = this.toIntegerQty(item._batchInputQty, 0);
      if (qty < 1) qty = 1;
      else if (qty > max) qty = max;
      item._batchInputQty = qty;
    },

    getSaveReceivedQty(item) {
      if (this.isItemNewBatchMode(item)) {
        return (
          this.toIntegerQty(item._originalReceivedQty, 0) +
          this.toIntegerQty(item._batchInputQty, 0)
        );
      }
      return this.toIntegerQty(item.receivedQty, 0);
    },

    formatDateTime(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    statusVariant(status) {
      const s = (status || "").toLowerCase();
      if (s === "closed") return "light-success";
      if (s === "awaiting_more_books") return "light-info";
      if (s === "pending_parent_confirm") return "light-warning";
      if (s === "partial") return "light-warning";
      return "light-secondary";
    },

    formatStatus(status) {
      const labels = {
        pending: "Pending",
        pending_parent_confirm: "Awaiting Parent",
        awaiting_more_books: "Batch Confirmed",
        partial: "Partial",
        closed: "Closed",
      };
      const s = (status || "pending").toLowerCase();
      return labels[s] || s.charAt(0).toUpperCase() + s.slice(1);
    },

    async loadClasses() {
      try {
        const obj = {
          url:
            this.apiBase() +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.campusID +
            "&dID=0",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        const data = response?.data !== undefined ? response.data : response;
        this.classesData = Array.isArray(data) ? data : [];
        this.classes = this.classesData
          .map((el) => el.cls)
          .filter((cls) => cls && cls.id);
      } catch (error) {
        console.error("Error loading classes:", error);
        this.classes = [];
        this.classesData = [];
      }
    },

    loadSectionsForClass(classId) {
      const selected = this.classesData.find(
        (c) => Number(c.cls?.id) === Number(classId)
      );
      this.filterSections = (selected?.sections || [])
        .filter((s) => s && s.id)
        .map((s) => ({
          id: s.id,
          section: s.section || s.name || `Section ${s.id}`,
        }));
    },

    onFilterClassChange() {
      this.filters.sectionID = null;
      this.loadSectionsForClass(this.filters.classID);
      this.resetStudentSearchState();
      this.onFilterChange();
    },

    onFilterSectionChange() {
      this.resetStudentSearchState();
      this.onFilterChange();
    },

    onFilterChange() {
      this.currentPage = 1;
      this.loadRecords();
    },

    resetStudentSearchState() {
      if (
        this.selectedStudent &&
        !this.matchesClassSectionFilter(this.selectedStudent)
      ) {
        this.selectedStudent = null;
      }
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
      this.studentSearchCache = {};
      if (this.studentSearchTimer) {
        clearTimeout(this.studentSearchTimer);
        this.studentSearchTimer = null;
      }
    },

    getSelectedClassName() {
      const selected = this.classes.find(
        (cls) => Number(cls.id) === Number(this.filters.classID)
      );
      return (selected?.name || "").trim().toLowerCase();
    },

    getSelectedSectionName() {
      const selected = this.filterSections.find(
        (section) => Number(section.id) === Number(this.filters.sectionID)
      );
      return (selected?.section || "").trim().toLowerCase();
    },

    matchesClassSectionFilter(student) {
      if (!student || !this.filters.classID) return true;

      const studentClassID =
        student.classID ?? student.clsID ?? student.classId ?? null;
      const studentSectionID =
        student.sectionID ?? student.secID ?? student.sectionId ?? null;

      const selectedClassName = this.getSelectedClassName();
      const selectedSectionName = this.getSelectedSectionName();
      const studentClassName = (student.className || "").trim().toLowerCase();
      const studentSectionName = (student.sectionName || "").trim().toLowerCase();

      const classMatchesById =
        studentClassID != null &&
        Number(studentClassID) === Number(this.filters.classID);
      const classMatchesByName =
        selectedClassName &&
        studentClassName &&
        studentClassName === selectedClassName;
      const classMatches =
        classMatchesById || classMatchesByName || !selectedClassName;

      if (!this.filters.sectionID) {
        return classMatches;
      }

      const sectionMatchesById =
        studentSectionID != null &&
        Number(studentSectionID) === Number(this.filters.sectionID);
      const sectionMatchesByName =
        selectedSectionName &&
        studentSectionName &&
        studentSectionName === selectedSectionName;
      const sectionMatches =
        sectionMatchesById || sectionMatchesByName || !selectedSectionName;

      return classMatches && sectionMatches;
    },

    getStudentSearchCacheKey(term) {
      return [
        this.filters.classID || 0,
        this.filters.sectionID || 0,
        term,
      ].join("|");
    },

    normalizeStudentSearchResults(response) {
      if (Array.isArray(response)) return response;
      if (response && Array.isArray(response.data)) return response.data;
      if (
        response &&
        response.status === "success" &&
        Array.isArray(response.data)
      ) {
        return response.data;
      }
      if (
        response &&
        response.data &&
        Array.isArray(response.data.data)
      ) {
        return response.data.data;
      }
      return [];
    },

    async searchStudentsByKeyword(keyword) {
      const response = await this.getString({
        url:
          this.apiBase() +
          "Students/Search?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.campusID +
          "&keyword=" +
          encodeURIComponent(keyword) +
          "&type=",
        token: this.$store.state.userData.token,
      });

      return this.normalizeStudentSearchResults(response);
    },

    async loadStudentsForClassSection() {
      const response = await this.getString({
        url:
          this.apiBase() +
          "Students/LoadData?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.campusID +
          "&clsID=" +
          this.filters.classID +
          "&section=" +
          this.filters.sectionID,
        token: this.$store.state.userData.token,
      });

      return this.normalizeStudentSearchResults(response);
    },

    onStudentSearchChange() {
      if (this.studentSearchTimer) clearTimeout(this.studentSearchTimer);
      this.studentSearchTimer = setTimeout(() => {
        this.performStudentSearch();
      }, 700);
    },

    onStudentSearchBlur() {
      setTimeout(() => {
        this.studentSearchFocused = false;
      }, 180);
    },

    clearStudentSearchQuery() {
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
      if (this.studentSearchTimer) {
        clearTimeout(this.studentSearchTimer);
      }
    },

    mapStudentSearchOption(student) {
      let className = "";
      let sectionName = "";

      if (student.meta_class) {
        const parts = student.meta_class.split(" - ");
        if (parts.length >= 2) {
          className = parts[0].trim();
          sectionName = parts[1].trim();
        } else if (parts.length === 1) {
          className = parts[0].trim();
        }
      } else {
        className = student.className || "";
        sectionName = student.sectionName || "";
      }

      return {
        id: student.id,
        name: student.name || student.studentName || "—",
        className,
        sectionName,
        grNo: student.grNo || student.gr || student.grno || "",
        classID:
          student.classID ??
          student.clsID ??
          student.classId ??
          null,
        sectionID:
          student.sectionID ??
          student.secID ??
          student.sectionId ??
          null,
      };
    },

    filterStudentsByKeyword(students, term) {
      return (students || []).filter((student) => {
        const mapped = this.mapStudentSearchOption(student);
        const haystack = [
          mapped.name,
          mapped.grNo,
          mapped.className,
          mapped.sectionName,
          student.father_name,
          student.fatherName,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(term);
      });
    },

    async performStudentSearch() {
      const term = (this.studentSearchQuery || "").trim().toLowerCase();
      if (term.length < 2) {
        this.filteredStudentOptions = [];
        return;
      }

      const cacheKey = this.getStudentSearchCacheKey(term);
      if (this.studentSearchCache[cacheKey]) {
        this.filteredStudentOptions = this.studentSearchCache[cacheKey];
        return;
      }

      const token = ++this.lastStudentSearchToken;
      this.studentSearchLoading = true;

      try {
        let studentsData = [];

        if (this.filters.classID && this.filters.sectionID) {
          studentsData = await this.loadStudentsForClassSection();
          studentsData = this.filterStudentsByKeyword(studentsData, term);
        } else {
          studentsData = await this.searchStudentsByKeyword(this.studentSearchQuery);
        }

        if (token !== this.lastStudentSearchToken) return;

        const mapped = studentsData
          .map((student) => this.mapStudentSearchOption(student))
          .filter((student) => this.matchesClassSectionFilter(student));
        this.filteredStudentOptions = mapped;
        this.studentSearchCache[cacheKey] = mapped;
      } catch (error) {
        console.error("Student search error:", error);
        if (token === this.lastStudentSearchToken) {
          this.filteredStudentOptions = [];
        }
      } finally {
        if (token === this.lastStudentSearchToken) {
          this.studentSearchLoading = false;
        }
      }
    },

    selectFilterStudent(student) {
      this.selectedStudent = { ...student };
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
      this.studentSearchFocused = false;
      this.currentPage = 1;
      this.loadRecords();
    },

    clearFilterStudent() {
      this.selectedStudent = null;
      this.studentSearchQuery = "";
      this.filteredStudentOptions = [];
      this.studentSearchFocused = false;
      this.currentPage = 1;
      this.loadRecords();
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadRecords();
    },

    async loadRecords(page) {
      if (page) this.currentPage = page;
      this.dataLoading = true;
      try {
        let url =
          this.apiBase() +
          "BookListRecord/GetByPagination?db=" +
          this.dbParam() +
          "&campusID=" +
          this.campusID +
          "&pageNumber=" +
          this.currentPage +
          "&pageSize=" +
          this.perPage;
        if (this.filters.classID) url += "&classID=" + this.filters.classID;
        if (this.filters.classID && this.filters.sectionID != null) {
          url += "&sectionID=" + this.filters.sectionID;
        }
        if (this.filters.status) url += "&status=" + this.filters.status;
        if (this.selectedStudent && this.selectedStudent.id) {
          url += "&sID=" + this.selectedStudent.id;
        }

        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
          returnData: true,
        });
        const { data, total } = this.normalizeListResponse(response);
        this.records = (Array.isArray(data) ? data : []).map((row) =>
          this.mapRecordRow(row)
        );
        this.totalCount = total;
      } catch (error) {
        console.error("Error loading book list records:", error);
        this.records = [];
        this.totalCount = 0;
      } finally {
        this.dataLoading = false;
      }
    },

    confirmGenerateRecords() {
      if (!this.rights.add || !this.canUseClassSection) return;
      Swal.fire({
        title: this.$t("Generate Records?"),
        text: this.$t(
          "This will create book list records for all students in the selected class and section, and send notifications to parents."
        ),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$t("Yes, generate"),
        cancelButtonText: this.$t("Cancel"),
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) this.generateRecords();
      });
    },

    async generateRecords() {
      if (!this.rights.add) return;
      this.generating = true;
      try {
        const url =
          this.apiBase() +
          "BookListRecord/GenerateRecords?db=" +
          this.dbParam() +
          "&campusID=" +
          this.campusID +
          "&classID=" +
          this.filters.classID +
          "&sectionID=" +
          this.filters.sectionID +
          "&userID=" +
          this.userID;
        await this.post({
          url,
          body: {},
          token: this.$store.state.userData.token,
          context: this,
        });
        Swal.fire({
          icon: "success",
          title: this.$t("Records generated"),
          text: this.$t("Book list records have been created for all students."),
          customClass: { confirmButton: "btn btn-success" },
          buttonsStyling: false,
        });
        this.currentPage = 1;
        await this.loadRecords();
      } catch (error) {
        console.error("Error generating records:", error);
        Swal.fire({
          icon: "error",
          title: this.$t("Error"),
          text: error?.message || this.$t("Failed to generate records."),
          customClass: { confirmButton: "btn btn-danger" },
          buttonsStyling: false,
        });
      } finally {
        this.generating = false;
      }
    },

    async openViewModal(row) {
      if (!this.rights.view) return;
      const recordId = this.resolveRecordId(row);
      if (!recordId) {
        Swal.fire({
          icon: "error",
          title: this.$t("Error"),
          text: this.$t("Invalid record id."),
          customClass: { confirmButton: "btn btn-danger" },
          buttonsStyling: false,
        });
        return;
      }

      this.viewLoading = true;
      this.resetViewForm();
      this.showViewModal = true;
      this.$forceUpdate();
      await this.$nextTick();

      try {
        this.viewForm = await this.fetchRecordDetail(recordId, row);
      } catch (error) {
        console.error("Error loading record details:", error);
        this.showViewModal = false;
        Swal.fire({
          icon: "error",
          title: this.$t("Error"),
          text: this.$t("Failed to load record details."),
          customClass: { confirmButton: "btn btn-danger" },
          buttonsStyling: false,
        });
      } finally {
        this.viewLoading = false;
      }
    },

    resetViewForm() {
      this.viewForm = {
        recordId: 0,
        studentName: "",
        grNo: "",
        className: "",
        sectionName: "",
        status: "",
        items: [],
        categoryRemarksMap: {},
        receivedHistory: [],
        parentConfirmHistory: [],
      };
    },

    onViewModalHidden() {
      this.resetViewForm();
      this.viewLoading = false;
    },

    async openEditModal(row) {
      if (!this.rights.edit) return;
      const recordId = this.resolveRecordId(row);
      if (!recordId) {
        Swal.fire({
          icon: "error",
          title: this.$t("Error"),
          text: this.$t("Invalid record id."),
          customClass: { confirmButton: "btn btn-danger" },
          buttonsStyling: false,
        });
        return;
      }

      this.editLoading = true;
      this.resetEditForm();
      this.showEditModal = true;
      this.$forceUpdate();
      await this.$nextTick();

      try {
        this.editForm = await this.fetchRecordDetail(recordId, row);
      } catch (error) {
        console.error("Error loading record:", error);
        if (!this.editForm.items.length) {
          this.showEditModal = false;
          Swal.fire({
            icon: "error",
            title: this.$t("Error"),
            text: this.$t("Failed to load record details."),
            customClass: { confirmButton: "btn btn-danger" },
            buttonsStyling: false,
          });
        }
      } finally {
        this.editLoading = false;
      }
    },

    resetEditForm() {
      this.editForm = {
        recordId: 0,
        studentName: "",
        grNo: "",
        className: "",
        sectionName: "",
        status: "",
        items: [],
        categoryRemarksMap: {},
      };
    },

    onEditModalHidden() {
      this.resetEditForm();
      this.editLoading = false;
    },

    async saveRecord() {
      if (!this.rights.edit || !this.hasEditChanges) return;

      const changedItems = this.editForm.items.filter((item) => {
        if (this.isItemNewBatchMode(item)) {
          return Number(item._batchInputQty) > 0;
        }
        return Number(item.receivedQty) !== Number(item._originalReceivedQty);
      });

      for (const item of changedItems) {
        if (this.isItemNewBatchMode(item)) {
          const batchQty = Number(item._batchInputQty);
          const maxBatch = Number(item.remainingQty) || 0;
          if (batchQty < 1 || batchQty > maxBatch) {
            Swal.fire({
              icon: "error",
              title: this.$t("Error"),
              text: this.$t("Enter a quantity between 1 and the remaining amount."),
              customClass: { confirmButton: "btn btn-danger" },
              buttonsStyling: false,
            });
            return;
          }
          const cumulative = this.getSaveReceivedQty(item);
          if (cumulative > Number(item.requiredQty)) {
            Swal.fire({
              icon: "error",
              title: this.$t("Error"),
              text: this.$t("Received quantity cannot exceed required quantity."),
              customClass: { confirmButton: "btn btn-danger" },
              buttonsStyling: false,
            });
            return;
          }
        } else {
          const qty = Number(item.receivedQty);
          const min = Number(item._originalReceivedQty) || 0;
          const max = Number(item.requiredQty) || 0;
          if (qty < min) {
            Swal.fire({
              icon: "error",
              title: this.$t("Error"),
              text: this.$t("Received quantity cannot be decreased."),
              customClass: { confirmButton: "btn btn-danger" },
              buttonsStyling: false,
            });
            return;
          }
          if (qty > max) {
            Swal.fire({
              icon: "error",
              title: this.$t("Error"),
              text: this.$t("Received quantity cannot exceed required quantity."),
              customClass: { confirmButton: "btn btn-danger" },
              buttonsStyling: false,
            });
            return;
          }
        }
      }

      this.saving = true;
      try {
        const url =
          this.apiBase() +
          "BookListRecord/Save?db=" +
          this.dbParam() +
          "&campusID=" +
          this.campusID +
          "&userID=" +
          this.userID;
        const payload = {
          record: { id: this.editForm.recordId },
          items: changedItems.map((item) => ({
            id: item.id,
            receivedQty: this.getSaveReceivedQty(item),
          })),
          categoryRemarks: this.getCategoryRemarksPayload(),
        };
        await this.post({
          url,
          body: payload,
          token: this.$store.state.userData.token,
          context: this,
        });
        this.showEditModal = false;
        await this.loadRecords();
        Swal.fire({
          icon: "success",
          title: this.$t("Saved"),
          text: this.$t("Record updated successfully."),
          customClass: { confirmButton: "btn btn-success" },
          buttonsStyling: false,
        });
      } catch (error) {
        console.error("Error saving record:", error);
        Swal.fire({
          icon: "error",
          title: this.$t("Error"),
          text: error?.message || this.$t("Failed to save record."),
          customClass: { confirmButton: "btn btn-danger" },
          buttonsStyling: false,
        });
      } finally {
        this.saving = false;
      }
    },

    openSummaryReport() {
      const routeData = this.$router.resolve({
        name: "book-list-summary-report",
        query: {
          db: this.$store.state.userData.db,
          cID: this.campusID,
          classID: this.filters.classID || "",
          sectionID: this.filters.sectionID || "",
          status: this.filters.status || "",
          sID: (this.selectedStudent && this.selectedStudent.id) || "",
        },
      });
      window.open(routeData.href, "_blank");
    },

    openCategoryReport() {
      const routeData = this.$router.resolve({
        name: "book-list-category-report",
        query: {
          db: this.$store.state.userData.db,
          cID: this.campusID,
          classID: this.filters.classID || "",
          sectionID: this.filters.sectionID || "",
          sID: (this.selectedStudent && this.selectedStudent.id) || "",
        },
      });
      window.open(routeData.href, "_blank");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../planner/plannerListTable.scss";

::v-deep .bl-edit-modal-content {
  border: 1px solid #e3e6f0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(34, 41, 47, 0.12);
}

::v-deep .bl-edit-modal-body {
  padding: 0;
}

.bl-edit-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.15rem 1.5rem;
  background: #fff;
  border-bottom: 1px solid #d8d6de;
}

.bl-edit-modal-title-wrap {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
}

.bl-edit-modal-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #f3f0ff;
  color: #7c3aed;
  flex-shrink: 0;
}

.bl-edit-modal-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #3d3f4f;
  letter-spacing: 0;
}

.bl-edit-modal-sub {
  margin-top: 0.2rem;
  font-size: 0.875rem;
  color: #6e6b7b;
  line-height: 1.4;
}

.bl-edit-modal-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: #ea5455;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: #d63031;
    transform: scale(1.05);
  }
}

.bl-edit-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem 1.5rem;
  color: #6e6b7b;
  font-size: 0.9rem;
}

.bl-edit-loading--overlay {
  min-height: 280px;
}

.bl-modal-overlay {
  min-height: 200px;
}

.bl-edit-body {
  padding: 1.5rem;
  background: #fff;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
}

.bl-edit-student-section {
  margin-bottom: 1rem !important;
}

.bl-edit-category-card {
  background: #fff;
  border: 1px solid #d8d6de;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 1.15rem;

  &:last-of-type {
    margin-bottom: 0;
  }
}

.bl-category-remarks {
  margin: 0 1rem 1rem;
  padding: 0.85rem 1rem;
  background: #f8f7ff;
  border: 1px solid #ebe7ff;
  border-radius: 10px;
}

.bl-category-remarks-label {
  display: flex;
  align-items: center;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #5e50ee;
  margin-bottom: 0.45rem;
}

.bl-category-remarks-name {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  color: #6e6b7b;
  margin-left: 0.15rem;
}

.bl-category-remarks-input {
  font-size: 0.9rem;
  border-radius: 8px !important;
  border-color: #d8d6f5 !important;
  background: #fff !important;
}

.bl-category-remarks--view {
  background: #f4f5f7;
  border-color: #e3e4ea;
}

.bl-category-remarks-text {
  font-size: 0.9rem;
  color: #2d1b69;
  line-height: 1.45;
  white-space: pre-wrap;
}

.bl-edit-category-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem;
  padding: 1rem 1.15rem;
  background: #f8f9fc;
  border-bottom: 1px solid #d8d6de;
}

.bl-edit-category-title {
  display: flex;
  align-items: center;
  gap: 0.85rem;

  strong {
    font-size: 1rem;
    font-weight: 700;
    color: #3d3f4f;
  }
}

.bl-edit-category-meta {
  font-size: 0.8125rem;
  color: #6e6b7b;
  margin-top: 0.2rem;
}

.bl-edit-category-count {
  font-size: 0.75rem;
  color: #8a86b7;
  font-weight: 600;
}

.bl-edit-line-items-wrap {
  padding: 0.85rem 1rem 1rem;
  background: #faf9ff;
}

.bl-view-line-items-wrap {
  background: #fff;
}

.bl-edit-line-items-hint {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.55rem 0.75rem;
  margin-bottom: 0.75rem;
  border-radius: 8px;
  background: #eef2ff;
  border: 1px solid #dbeafe;
  color: #4338ca;
  font-size: 0.78rem;
  font-weight: 600;
}

.bl-edit-line-card {
  background: #fff;
  border: 1px solid #e0d8ff;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 0.75rem;
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.06);

  &:last-child {
    margin-bottom: 0;
  }

  &--done {
    border-color: #bbf7d0;
    background: linear-gradient(180deg, #f0fdf4 0%, #fff 100%);
  }
}

.bl-edit-line-card__header {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #f0edff;
}

.bl-edit-line-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
  color: #fff;
  font-size: 0.78rem;
  font-weight: 800;
  flex-shrink: 0;
}

.bl-edit-line-card--done .bl-edit-line-index {
  background: linear-gradient(135deg, #28c76f 0%, #48da89 100%);
}

.bl-edit-line-info {
  flex: 1;
  min-width: 0;
}

.bl-edit-line-title {
  display: block;
  font-size: 0.95rem;
  font-weight: 700;
  color: #2d1b69;
  line-height: 1.35;
}

.bl-edit-line-detail {
  display: block;
  margin-top: 0.2rem;
  font-size: 0.78rem;
  color: #8a86b7;
  line-height: 1.35;
}

.bl-edit-line-required-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  padding: 0.45rem 0.65rem;
  border-radius: 10px;
  background: #fff7ed;
  border: 1.5px solid #fed7aa;
  flex-shrink: 0;
}

.bl-edit-line-required-label {
  font-size: 0.62rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #c2410c;
  line-height: 1.2;
  text-align: center;
}

.bl-edit-line-required-value {
  font-size: 1.35rem;
  font-weight: 800;
  color: #ea580c;
  line-height: 1.1;
}

.bl-edit-line-progress {
  padding: 0.75rem 1rem 0.85rem;
  background: #fcfbff;
  border-bottom: 1px solid #f3f0ff;
}

.bl-edit-line-progress-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.45rem;
}

.bl-edit-line-progress-text {
  font-size: 0.82rem;
  color: #5e5873;

  strong {
    color: #7367f0;
    font-weight: 800;
  }
}

.bl-edit-line-status {
  font-size: 0.68rem !important;
  font-weight: 700;
  padding: 0.3rem 0.55rem !important;
}

.bl-edit-line-progress-track {
  height: 7px;
  border-radius: 999px;
  background: #ede9fe;
  overflow: hidden;
}

.bl-edit-line-progress-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #7367f0 0%, #9e95f5 100%);
  transition: width 0.25s ease;
}

.bl-edit-line-card--done .bl-edit-line-progress-fill {
  background: linear-gradient(90deg, #28c76f 0%, #48da89 100%);
}

.bl-edit-line-action {
  padding: 0.75rem 1rem 0.85rem;

  &--done {
    padding-top: 0.65rem;
    padding-bottom: 0.65rem;
  }
}

.bl-edit-line-confirmed-banner,
.bl-edit-line-wait-banner {
  display: flex;
  align-items: center;
  padding: 0.55rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8rem;
  margin-bottom: 0.65rem;
}

.bl-edit-line-confirmed-banner {
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
  color: #166534;
}

.bl-edit-line-wait-banner {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
}

.bl-edit-stats-row--compact {
  grid-template-columns: repeat(auto-fit, minmax(88px, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.65rem;
}

.bl-edit-stat--compact {
  padding: 0.55rem 0.6rem;

  .bl-edit-stat-label {
    font-size: 0.62rem;
    margin-bottom: 0.2rem;
  }

  .bl-edit-stat-value {
    font-size: 1rem;
  }
}

.bl-edit-input-panel--line {
  margin-top: 0;
  border-color: #c4b5fd;
  background: #faf9ff;
}

.bl-view-line-card .bl-view-line-stats {
  padding: 0.75rem 1rem 0.85rem;
  background: #fff;
}

.bl-edit-batch-card {
  padding: 1rem 1.1rem;
  border-bottom: 1px solid #f5f3ff;

  &:last-child {
    border-bottom: none;
  }

  &--done {
    background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
  }

  &--active {
    background: linear-gradient(180deg, #fffdf8 0%, #fff 100%);
    border-left: 3px solid #7367f0;
  }
}

.bl-edit-batch-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
}

.bl-edit-batch-type {
  display: inline-flex;
  align-items: center;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #5e5873;
}

.bl-edit-status-badge {
  font-size: 0.8125rem !important;
  font-weight: 700;
  padding: 0.4rem 0.85rem !important;
}

.bl-edit-stats-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  margin-bottom: 0.25rem;
}

.bl-edit-stat {
  background: #fff;
  border: 1px solid #d8d6de;
  border-radius: 10px;
  padding: 0.75rem 0.85rem;
  text-align: center;
}

.bl-edit-stat-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.35px;
  color: #6e6b7b;
  margin-bottom: 0.35rem;
  line-height: 1.25;
}

.bl-edit-stat-value {
  display: block;
  font-size: 1.25rem;
  font-weight: 800;
  color: #3d3f4f;
  line-height: 1.15;

  &.bl-stat--success {
    color: #28c76f;
  }

  &.bl-stat--info {
    color: #7367f0;
  }

  &.bl-stat--warning {
    color: #ff9f43;
  }

  &.bl-stat--primary {
    color: #7367f0;
  }
}

.bl-edit-input-panel {
  margin-top: 0.85rem;
  padding: 0.85rem 1rem;
  background: #fff;
  border: 1.5px dashed #cbb9ff;
  border-radius: 12px;
}

.bl-edit-input-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #7367f0;
  margin-bottom: 0.5rem;
}

.bl-edit-input-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.bl-edit-qty-input {
  width: 110px;
  height: 44px;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 800;
  color: #2d1b69;
  border: 2px solid #e0d8ff;
  border-radius: 10px;
  background: #faf9ff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: #7367f0;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.15);
    background: #fff;
  }
}

.bl-edit-input-range {
  font-size: 0.78rem;
  color: #8a86b7;

  strong {
    color: #7367f0;
    font-weight: 800;
  }
}

.bl-edit-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.65rem;
  margin-top: 1.25rem;
  padding-top: 1.15rem;
  border-top: 1px solid #f0edff;
}

.bl-edit-btn-cancel,
.bl-edit-btn-save {
  min-width: 120px;
  height: 42px;
  border-radius: 10px !important;
  font-weight: 700;
  font-size: 0.88rem;
}

.bl-edit-btn-save {
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%) !important;
  border: none !important;
  box-shadow: 0 4px 14px rgba(115, 103, 240, 0.35);

  &:disabled {
    opacity: 0.55;
    box-shadow: none;
  }
}

@media (max-width: 576px) {
  .details-meta-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .bl-edit-stats-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .bl-edit-body {
    padding: 1rem;
  }

  .bl-edit-modal-header {
    padding: 1rem;
  }

  .bl-edit-input-row {
    flex-direction: column;
    align-items: flex-start;
  }
}

::v-deep .bl-view-modal-content {
  border: none;
}

.bl-view-body {
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  padding-bottom: 0.75rem;
}

.bl-view-stats-row {
  padding: 1rem 1.15rem 0.85rem;
}

.bl-view-item-history {
  border-top: 1px solid #d8d6de;
  padding: 0.9rem 1.15rem 1rem;
  background: #f8f9fc;
}

.bl-view-mini-history {
  &:not(:last-child) {
    margin-bottom: 0.7rem;
    padding-bottom: 0.7rem;
    border-bottom: 1px dashed #e8e4ff;
  }
}

.bl-view-mini-history-label {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.35px;
  color: #5e5873;
  margin-bottom: 0.6rem;
}

.bl-view-mini-history-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem 0.75rem;
  padding: 0.65rem 0.85rem;
  margin-bottom: 0.45rem;
  background: #fff;
  border: 1px solid #d8d6de;
  border-radius: 10px;

  &:last-child {
    margin-bottom: 0;
  }
}

.bl-view-mini-history-date {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #3d3f4f;
}

.bl-view-mini-history-meta {
  font-size: 0.8125rem;
  color: #6e6b7b;
  flex: 1;
  min-width: 160px;
}

.bl-view-mini-history-by {
  width: 100%;
  font-size: 0.75rem;
  color: #8a8a9a;
  padding-top: 0.2rem;
  border-top: 1px dashed #e3e6f0;
  margin-top: 0.2rem;
}

.bl-view-history-section {
  background: #fff;
  border: 1px solid #d8d6de;
  border-radius: 10px;
  overflow: hidden;
}

::v-deep .bl-view-tabs-nav {
  background: #f8f9fc;
  border-bottom: 1px solid #d8d6de;
  padding: 0.5rem 0.85rem 0;
  margin-bottom: 0 !important;

  .nav-link {
    border: none !important;
    border-radius: 8px 8px 0 0 !important;
    font-size: 0.875rem;
    font-weight: 600;
    color: #6e6b7b;
    padding: 0.7rem 1rem;
    display: inline-flex;
    align-items: center;
    transition: color 0.2s ease, background 0.2s ease;

    &:hover {
      color: #7367f0;
    }

    &.active {
      color: #7367f0 !important;
      background: #fff !important;
      box-shadow: 0 -2px 10px rgba(115, 103, 240, 0.1);
    }
  }
}

::v-deep .bl-view-tabs-content {
  padding: 1rem 1.15rem 1.15rem;
  max-height: 360px;
  overflow-y: auto;
  background: #fff;
}

.bl-view-tab-count {
  font-size: 0.62rem !important;
  font-weight: 700;
}

.bl-view-history-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  color: #6e6b7b;
  font-size: 0.9rem;
  background: #f8f9fc;
  border-radius: 10px;
  border: 1px dashed #d8d6de;
}

.bl-history-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.bl-history-card {
  border: 1px solid #d8d6de;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.bl-history-card-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1.25rem;
  padding: 0.85rem 1rem;
  background: #f8f9fc;
  border-bottom: 1px solid #d8d6de;
}

.bl-history-card-meta {
  display: inline-flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: #3d3f4f;
}

.bl-history-table-wrap {
  overflow-x: auto;
}

.bl-history-table {
  width: 100%;
  border-collapse: collapse;

  thead th {
    background: #fff;
    color: #6e6b7b;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.35px;
    padding: 0.7rem 1rem;
    border-bottom: 1px solid #e3e6f0;
    white-space: nowrap;
  }

  tbody td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #f1f2f4;
    vertical-align: middle;
    font-size: 0.875rem;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.bl-history-cat {
  font-weight: 700;
  color: #3d3f4f;
  min-width: 100px;
}

.bl-history-detail {
  color: #6e6b7b;
  line-height: 1.4;
}

.bl-view-tab-count {
  position: relative;
  padding-left: 0.25rem;
}

.bl-view-timeline-entry {
  display: flex;
  gap: 0.75rem;
  padding-bottom: 1rem;
  position: relative;

  &:not(:last-child)::before {
    content: "";
    position: absolute;
    left: 6px;
    top: 20px;
    bottom: 0;
    width: 2px;
    background: linear-gradient(180deg, #cbb9ff 0%, #f0edff 100%);
  }

  &:last-child {
    padding-bottom: 0;
  }
}

.bl-view-timeline-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 6px;
  box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
}

.bl-view-timeline-entry--received .bl-view-timeline-dot {
  background: #28c76f;
  border: 2px solid #d1fae5;
}

.bl-view-timeline-entry--confirm .bl-view-timeline-dot {
  background: #7367f0;
  border: 2px solid #ede9fe;
}

.bl-view-timeline-card {
  flex: 1;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #f0edff;
  border-radius: 12px;
  padding: 0.8rem 0.95rem;
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.06);
}

.bl-view-timeline-head {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  margin-bottom: 0.65rem;
  padding-bottom: 0.55rem;
  border-bottom: 1px dashed #ebe9f1;
}

.bl-view-timeline-date,
.bl-view-timeline-by {
  display: inline-flex;
  align-items: center;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #3d3f4f;
}

.bl-view-timeline-summary {
  margin-bottom: 0.5rem;
}

.bl-view-timeline-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.45rem 0.65rem;
  padding: 0.4rem 0.5rem;
  border-radius: 8px;

  &:not(:last-child) {
    border-bottom: 1px dashed #f0edff;
    margin-bottom: 0.25rem;
    padding-bottom: 0.5rem;
  }

  &:hover {
    background: rgba(115, 103, 240, 0.04);
  }
}

.bl-view-timeline-cat {
  font-size: 0.82rem;
  font-weight: 700;
  color: #2d1b69;
  min-width: 80px;
}

.bl-view-timeline-meta {
  font-size: 0.72rem;
  color: #8a86b7;
}

.bl-view-footer {
  margin-top: 0.5rem;
}

@media (max-width: 576px) {
  .bl-view-body {
    padding: 1rem;
  }

  .bl-view-mini-history-row {
    flex-direction: column;
    align-items: flex-start;
  }
}

.leave-details-section {
  background: #fff;
  border: 1px solid #d8d6de;
  border-radius: 10px;
  padding: 1.15rem 1.25rem;
  position: relative;

  &--items,
  &--history {
    overflow: visible;
  }

  &:not(.leave-details-section--items):not(.leave-details-section--history) {
    overflow: hidden;
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    margin-bottom: 1.1rem;
    padding-bottom: 0.85rem;
    border-bottom: 1px solid #d8d6de;
  }

  .section-icon {
    color: #7c3aed;
    stroke-width: 2.25;
    flex-shrink: 0;
  }

  .section-title {
    font-size: 1rem;
    font-weight: 700;
    color: #3d3f4f;
    text-transform: none;
    letter-spacing: 0;
  }
}

.details-meta-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem;
}

.details-meta-item {
  background: #f8f9fc;
  border-radius: 10px;
  padding: 0.9rem 1rem;
  border: 1px solid #d8d6de;

  &--skeleton {
    min-height: 80px;
  }

  .details-label {
    display: block;
    font-size: 0.72rem;
    text-transform: uppercase;
    color: #6e6b7b;
    letter-spacing: 0.5px;
    font-weight: 700;
    margin-bottom: 0.45rem;
  }

  .details-value {
    display: block;
    font-size: 1.05rem;
    font-weight: 700;
    color: #3d3f4f;
    line-height: 1.35;
    word-break: break-word;

    &.details-value-primary {
      color: #7c3aed;
    }

    &.details-value-info {
      color: #00cfe8;
    }
  }
}

.bl-view-item-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f3f0ff;
  color: #7c3aed;
  flex-shrink: 0;
}

.bl-items-table {
  thead th {
    background: #f8f7ff !important;
    color: #2d1b69 !important;
    font-weight: 700;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-color: #e0d8ff !important;
    white-space: nowrap;
  }

  tbody td {
    vertical-align: middle;
    border-color: #f0edff !important;
  }
}

.record-qty-badge {
  min-width: 34px;
  font-weight: 700;
  font-size: 0.8125rem;
  padding: 0.35rem 0.55rem;
}

.record-status-badge {
  font-size: 0.8125rem;
  font-weight: 700;
  padding: 0.4rem 0.85rem;
}

.record-count-badge {
  font-weight: 700;
  font-size: 0.75rem;
}

.record-items-table-wrap {
  max-height: 260px;
  overflow-y: auto;
  border-radius: 10px;
  border: 1px solid #f0edff;
}

.record-empty-state {
  text-align: center;
  padding: 2rem 1rem;
  color: #8a86b7;
  background: #faf9ff;
  border-radius: 12px;
  border: 1px dashed #e0d8ff;
}

.record-history-block {
  border: 1px solid #ebe9f1;
  border-radius: 12px;
  padding: 0.85rem 1rem;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);

  &--skeleton {
    background: #fff;
  }
}

.record-history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  padding-bottom: 0.65rem;
  border-bottom: 1px solid #f0edff;
}

.record-history-title {
  display: flex;
  align-items: center;
  color: #2d1b69;
  font-size: 0.9rem;
}

.record-history-count {
  font-size: 0.7rem;
  font-weight: 600;
}

.record-history-timeline {
  position: relative;
  padding-left: 0.5rem;
}

.record-history-entry {
  display: flex;
  gap: 0.75rem;
  padding-bottom: 0.85rem;
  position: relative;

  &:not(:last-child)::before {
    content: "";
    position: absolute;
    left: 5px;
    top: 18px;
    bottom: 0;
    width: 2px;
    background: linear-gradient(180deg, #cbb9ff 0%, #f0edff 100%);
  }

  &:last-child {
    padding-bottom: 0;
  }
}

.record-history-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #7c5dfa;
  border: 2px solid #ede9fe;
  flex-shrink: 0;
  margin-top: 4px;
  box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.15);
}

.record-history-content {
  flex: 1;
  background: #fff;
  border: 1px solid #f0edff;
  border-radius: 10px;
  padding: 0.65rem 0.85rem;
}

.record-history-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;

  &:not(:last-child) {
    margin-bottom: 0.4rem;
    padding-bottom: 0.4rem;
    border-bottom: 1px dashed #f5f3ff;
  }
}

.record-history-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #8a86b7;
  font-weight: 700;
}

.record-history-value {
  font-size: 0.82rem;
  font-weight: 600;
  color: #2d1b69;
  display: inline-flex;
  align-items: center;
}

.record-history-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.85rem;
  background: #fafafa;
  border-radius: 8px;
  color: #8a86b7;
  font-size: 0.82rem;
}

.record-close-btn {
  min-width: 90px;
  border-radius: 8px;
}

.record-view-skeleton {
  .section-header {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 0.85rem;
    border-bottom: 2px solid #f0edff;
  }
}

.filter-fields-group--cols-4,
.filter-fields-group--cols-5 {
  .filter-field-item:not(.filter-student-search) {
    min-width: 145px;
    max-width: 175px;
    flex: 0 1 160px !important;
  }

  .filter-student-search {
    min-width: 160px;
    max-width: 240px;
    flex: 1 1 200px !important;
  }
}

.book-list-record-toolbar {
  flex-wrap: nowrap !important;
  gap: 0.35rem;
  min-width: 0;

  .filter-fields-group {
    flex-wrap: nowrap !important;
    min-width: 0;
    flex: 1 1 auto;
  }

  .filter-actions-group {
    flex-wrap: nowrap !important;
    flex-shrink: 0;
    margin-left: 0.35rem !important;
  }
}

.filter-field-compact {
  ::v-deep .vs__dropdown-toggle {
    min-height: 40px !important;
    padding: 0 0.35rem !important;
  }

  ::v-deep .vs__selected {
    font-size: 0.8rem !important;
    margin: 2px 0 !important;
  }

  ::v-deep .vs__search {
    font-size: 0.8rem !important;
  }

  ::v-deep .vs__placeholder {
    font-size: 0.8rem !important;
  }
}

.action-btn-filter--compact {
  height: 40px !important;
  min-height: 40px !important;
  min-width: 108px !important;
  padding: 0 0.85rem !important;
  font-size: 0.82rem !important;
  border-radius: 8px !important;
}

.book-list-record-table {
  width: max-content !important;
  min-width: 100% !important;
  table-layout: auto;

  ::v-deep th,
  ::v-deep td {
    padding: 0.75rem 0.875rem !important;
    line-height: 1.45 !important;
  }

  ::v-deep th {
    font-size: 0.75rem !important;
    font-weight: 700 !important;
    letter-spacing: 0.45px;
    white-space: nowrap;
    color: #1e293b !important;
  }

  ::v-deep td {
    font-size: 0.9rem !important;
    font-weight: 500 !important;
    color: #334155 !important;
  }

  ::v-deep .col-sn {
    width: 48px;
    font-weight: 600 !important;
    color: #64748b !important;
  }

  ::v-deep .col-gr {
    width: 108px;
    white-space: nowrap;
    font-weight: 600 !important;
    color: #7c5dfa !important;
  }

  ::v-deep .col-student {
    width: 1%;
    min-width: 160px;
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .student-name-cell {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600 !important;
    color: #2d1b69 !important;
  }

  ::v-deep .col-cls,
  ::v-deep .col-sec {
    font-weight: 600 !important;
    color: #475569 !important;
  }

  ::v-deep .col-cls {
    width: 64px;
    white-space: nowrap;
  }

  ::v-deep .col-sec {
    width: 76px;
    white-space: nowrap;
  }

  ::v-deep .col-sts {
    width: 96px;
    white-space: nowrap;
  }

  ::v-deep .col-act {
    width: 96px;
    white-space: nowrap;
  }
}

.record-status-badge-sm {
  font-size: 0.75rem !important;
  font-weight: 700;
  padding: 0.3rem 0.6rem !important;
  line-height: 1.2;
  letter-spacing: 0.2px;
}

.record-actions-cell {
  gap: 0.35rem;
}

.action-btn-outline--sm,
.action-btn-table--sm {
  width: 34px !important;
  height: 34px !important;
  min-width: 34px !important;
  border-radius: 8px !important;
}

.filter-student-search {
  position: relative;
}

.student-search-box {
  position: relative;

  &--active .student-search-input-wrap {
    border-color: #7367f0;
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
  }
}

.student-search-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 40px;
  border: 1.5px solid #e0d8ff;
  border-radius: 10px;
  background: #fff;
  transition: all 0.2s ease;
}

.student-search-icon {
  position: absolute;
  left: 0.85rem;
  color: #7367f0;
  pointer-events: none;
  z-index: 1;
}

.student-search-input {
  border: none !important;
  box-shadow: none !important;
  height: 38px !important;
  min-height: 38px !important;
  padding-left: 2.15rem !important;
  padding-right: 2rem !important;
  font-size: 0.8rem !important;
  background: transparent !important;

  &:focus {
    box-shadow: none !important;
    border-color: transparent !important;
  }
}

.student-search-clear,
.student-search-spinner {
  position: absolute;
  right: 0.65rem;
  z-index: 2;
}

.student-search-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #f3f2f7;
  color: #6e6b7b;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #ffe2e5;
    color: #ea5455;
  }
}

.student-search-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 1060;
  background: #fff;
  border: 1px solid #e0d8ff;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(115, 103, 240, 0.16);
  overflow: hidden;
}

.student-search-dropdown__header {
  padding: 0.55rem 0.85rem;
  background: linear-gradient(180deg, #faf9ff 0%, #f8f7ff 100%);
  border-bottom: 1px solid #f0edff;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #8a86b7;
}

.student-search-hint,
.student-search-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 0.85rem;
  color: #6e6b7b;
  font-size: 0.82rem;
  text-align: center;
}

.student-search-state {
  flex-direction: column;
  padding: 1.25rem 0.85rem;

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: #f8f7ff;
    color: #7367f0;
    margin-bottom: 0.25rem;
  }

  strong {
    color: #2d1b69;
    font-size: 0.88rem;
  }

  small {
    color: #8a86b7;
    font-size: 0.75rem;
  }
}

.student-options {
  max-height: 280px;
  overflow-y: auto;
}

.student-option {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.7rem 0.85rem;
  border: none;
  border-bottom: 1px solid #f3f2f7;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(90deg, #faf9ff 0%, #fff 100%);
  }

  &:last-child {
    border-bottom: none;
  }

  &__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: rgba(115, 103, 240, 0.12);
    color: #7367f0;
    flex-shrink: 0;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__name {
    display: block;
    font-size: 0.88rem;
    font-weight: 700;
    color: #2d1b69;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__meta {
    display: block;
    font-size: 0.74rem;
    color: #8a86b7;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__gr {
    flex-shrink: 0;
    font-size: 0.68rem;
    font-weight: 700;
    color: #7367f0;
    background: #f0edff;
    border-radius: 999px;
    padding: 0.2rem 0.55rem;
  }
}

.selected-student-chip {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-height: 40px;
  padding: 0.25rem 0.4rem 0.25rem 0.5rem;
  border: 1.5px solid #cbb9ff;
  border-radius: 10px;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.08);

  &__avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
    color: #fff;
    flex-shrink: 0;
  }

  &__body {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 0.35rem;
  }

  &__name {
    display: block;
    font-size: 0.78rem;
    font-weight: 700;
    color: #2d1b69;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.2;
    min-width: 0;
  }

  &__meta {
    display: none;
  }

  &__clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    border-radius: 8px;
    background: #fff;
    color: #6e6b7b;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
      background: #ffe2e5;
      color: #ea5455;
    }
  }
}

.meta-tag {
  display: inline-block;
  font-size: 0.68rem;
  font-weight: 600;
  color: #7367f0;
  background: #f0edff;
  border-radius: 999px;
  padding: 0.1rem 0.45rem;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &--gr {
    color: #06b6d4;
    background: rgba(6, 182, 212, 0.12);
  }
}

.student-search-fade-enter-active,
.student-search-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.student-search-fade-enter,
.student-search-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.student-search-dropdown::-webkit-scrollbar,
.student-options::-webkit-scrollbar {
  width: 5px;
}

.student-search-dropdown::-webkit-scrollbar-thumb,
.student-options::-webkit-scrollbar-thumb {
  background: #d8d6de;
  border-radius: 999px;
}
</style>
